import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatSiretDisplay } from '../../utils/siret';
import type { InvoiceBuyerProfile, InvoiceSellerProfile } from '../../types/invoiceSeller';
import type { InvoiceLineItem } from '../../types';

export interface InvoicePreviewModel {
  invoiceNumber: string;
  invoiceName?: string;
  issuedAtLabel: string;
  seller: InvoiceSellerProfile;
  buyer: InvoiceBuyerProfile;
  projectName: string;
  amountLabel: string;
  paymentDateLabel?: string;
  statusLabel: string;
  notes?: string;
  paymentTerms?: string;
  paymentMethodLabel?: string;
  accountHolder?: string;
  iban?: string;
  bic?: string;
  lineItems: Array<
    InvoiceLineItem & { totalLabel: string; unitPriceLabel: string; vatLabel: string }
  >;
  labels: {
    preview: string;
    title: string;
    from: string;
    to: string;
    project: string;
    amount: string;
    paymentDate: string;
    status: string;
    notes: string;
    paymentTerms: string;
    paymentSection: string;
    paymentMethod: string;
    accountHolder: string;
    ibanLabel: string;
    bicLabel: string;
    siret: string;
    vatNumber: string;
    issuedAt: string;
    item: string;
    qty: string;
    unitPrice: string;
    vatRate: string;
    total: string;
  };
}

type PreviewLine = InvoicePreviewModel['lineItems'][number];

type PreviewPage = {
  items: PreviewLine[];
  showDocumentHeader: boolean;
  showFooter: boolean;
};

/** Vertical budget in line-item units (tuned so pages split only when content overflows). */
const PAGE1_CAPACITY = 18;
const CONTINUATION_CAPACITY = 28;
const FOOTER_BASE = 2.5;

const addressLinesFromParty = (party: InvoiceSellerProfile | InvoiceBuyerProfile): string[] =>
  [
    party.addressLine1,
    party.addressLine2,
    [party.postalCode, party.city].filter(Boolean).join(' ').trim() || null,
    party.countryCode,
  ].filter((line): line is string => Boolean(line && String(line).trim()));

const estimateItemUnits = (item: PreviewLine): number => {
  const details = item.details?.trim();
  if (!details) return 1;
  const wrapped = Math.ceil(details.length / 90);
  const hardBreaks = details.split(/\r?\n/).length;
  return 1 + Math.min(2.5, Math.max(wrapped, hardBreaks) * 0.4);
};

const estimateFooterUnits = (model: InvoicePreviewModel): number => {
  let units = FOOTER_BASE;
  if (model.paymentTerms?.trim()) units += 1.2;
  if (
    model.paymentMethodLabel ||
    model.accountHolder?.trim() ||
    model.iban?.trim() ||
    model.bic?.trim()
  ) {
    units += 1.5;
  }
  if (model.notes?.trim()) {
    const noteLen = model.notes.trim().length;
    units += 1 + Math.min(2, Math.ceil(noteLen / 160));
  }
  return units;
};

const paginateLineItems = (
  items: PreviewLine[],
  footerUnits: number
): PreviewPage[] => {
  if (items.length === 0) {
    return [{ items: [], showDocumentHeader: true, showFooter: true }];
  }

  const pages: PreviewPage[] = [];
  let index = 0;
  let pageIndex = 0;

  while (index < items.length) {
    const isFirst = pageIndex === 0;
    const capacity = isFirst ? PAGE1_CAPACITY : CONTINUATION_CAPACITY;
    const chunk: PreviewLine[] = [];
    let used = 0;

    while (index < items.length) {
      const next = estimateItemUnits(items[index]);
      if (chunk.length > 0 && used + next > capacity) break;
      chunk.push(items[index]);
      used += next;
      index += 1;
      if (chunk.length === 1 && next > capacity) break;
    }

    pages.push({
      items: chunk,
      showDocumentHeader: isFirst,
      showFooter: false,
    });
    pageIndex += 1;
  }

  const last = pages[pages.length - 1];
  const lastUsed = last.items.reduce((sum, item) => sum + estimateItemUnits(item), 0);
  const lastCapacity = last.showDocumentHeader ? PAGE1_CAPACITY : CONTINUATION_CAPACITY;

  // Keep totals on the last content page whenever possible; new page only if clearly over.
  if (lastUsed + footerUnits <= lastCapacity + 4) {
    last.showFooter = true;
  } else {
    pages.push({ items: [], showDocumentHeader: false, showFooter: true });
  }

  return pages;
};

const PartyBlock: React.FC<{
  party: InvoiceSellerProfile | InvoiceBuyerProfile;
  labels: { siret: string; vatNumber: string };
}> = ({ party, labels }) => {
  const lines = addressLinesFromParty(party);
  const name = party.name?.trim();
  const company = party.company?.trim();
  const headline = name || company || '—';
  return (
    <div className="space-y-1 min-w-0">
      <div className="font-bold text-gray-950 truncate">{headline}</div>
      {name && company && company !== name ? (
        <div className="text-gray-600">{company}</div>
      ) : null}
      {party.siret ? (
        <div className="tabular-nums text-gray-500">
          {labels.siret}: {formatSiretDisplay(party.siret)}
        </div>
      ) : null}
      {party.vatNumber ? (
        <div className="text-gray-500">
          {labels.vatNumber}: {party.vatNumber}
        </div>
      ) : null}
      {lines.map((line) => (
        <div key={line} className="text-gray-500">
          {line}
        </div>
      ))}
    </div>
  );
};

const LineItemsTable: React.FC<{
  items: PreviewLine[];
  labels: InvoicePreviewModel['labels'];
  showVat: boolean;
}> = ({ items, labels: L, showVat }) => (
  <div>
    <div
      className={`grid gap-2 border-b border-gray-200 pb-2 text-[8pt] font-semibold uppercase tracking-wide text-gray-400 ${
        showVat
          ? 'grid-cols-[minmax(0,1.3fr)_0.4fr_0.65fr_0.7fr_0.65fr]'
          : 'grid-cols-[minmax(0,1.3fr)_0.4fr_0.65fr_0.65fr]'
      }`}
    >
      <span>{L.item}</span>
      <span className="text-right">{L.qty}</span>
      <span className="text-right">{L.unitPrice}</span>
      {showVat ? <span className="text-right">{L.vatRate}</span> : null}
      <span className="text-right">{L.total}</span>
    </div>
    {items.map((line) => (
      <div
        key={line.id}
        className={`grid gap-2 border-b border-gray-100 py-2.5 ${
          showVat
            ? 'grid-cols-[minmax(0,1.3fr)_0.4fr_0.65fr_0.7fr_0.65fr]'
            : 'grid-cols-[minmax(0,1.3fr)_0.4fr_0.65fr_0.65fr]'
        }`}
      >
        <span className="min-w-0 text-gray-800">
          <span className="block truncate">{line.description || '—'}</span>
          {line.details?.trim() ? (
            <span className="mt-0.5 block text-[8pt] leading-snug text-gray-500 whitespace-pre-wrap">
              {line.details.trim()}
            </span>
          ) : null}
        </span>
        <span className="text-right tabular-nums text-gray-700">{line.quantity}</span>
        <span className="text-right tabular-nums text-gray-700">{line.unitPriceLabel}</span>
        {showVat ? (
          <span className="text-right text-gray-700">{line.vatLabel || ''}</span>
        ) : null}
        <span className="text-right font-medium tabular-nums text-gray-900">
          {line.totalLabel}
        </span>
      </div>
    ))}
  </div>
);

const InvoiceFooter: React.FC<{ model: InvoicePreviewModel }> = ({ model }) => {
  const L = model.labels;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <div className="w-[52%] space-y-1.5 text-[10pt]">
          <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-3">
            <span className="font-semibold text-gray-950">{L.total}</span>
            <span className="text-[12pt] font-bold tabular-nums text-gray-950">
              {model.amountLabel}
            </span>
          </div>
          {model.paymentDateLabel ? (
            <div className="flex justify-between gap-4 text-gray-500">
              <span>{L.paymentDate}</span>
              <span>{model.paymentDateLabel}</span>
            </div>
          ) : null}
        </div>
      </div>

      {model.paymentTerms?.trim() ? (
        <div>
          <div className="text-[8pt] font-semibold uppercase tracking-wide text-gray-400">
            {L.paymentTerms}
          </div>
          <div className="mt-1 whitespace-pre-wrap text-gray-700">
            {model.paymentTerms.trim()}
          </div>
        </div>
      ) : null}

      {model.paymentMethodLabel ||
      model.accountHolder?.trim() ||
      model.iban?.trim() ||
      model.bic?.trim() ? (
        <div>
          <div className="text-[8pt] font-semibold uppercase tracking-wide text-gray-400">
            {L.paymentSection}
          </div>
          <div className="mt-1 space-y-0.5 text-gray-700">
            {model.paymentMethodLabel ? (
              <div>
                {L.paymentMethod}: {model.paymentMethodLabel}
              </div>
            ) : null}
            {model.accountHolder?.trim() ? (
              <div>
                {L.accountHolder}: {model.accountHolder.trim()}
              </div>
            ) : null}
            {model.iban?.trim() ? (
              <div className="tabular-nums">
                {L.ibanLabel}: {model.iban.trim()}
              </div>
            ) : null}
            {model.bic?.trim() ? (
              <div className="tabular-nums">
                {L.bicLabel}: {model.bic.trim()}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {model.notes?.trim() ? (
        <div className="rounded-lg bg-gray-50 px-3 py-3">
          <div className="text-[8pt] font-semibold uppercase tracking-wide text-gray-400">
            {L.notes}
          </div>
          <div className="mt-1 whitespace-pre-wrap text-gray-700">{model.notes.trim()}</div>
        </div>
      ) : null}
    </div>
  );
};

const A4Page: React.FC<{
  children: React.ReactNode;
  footer?: React.ReactNode;
  pageNumber: number;
  pageCount: number;
  /** Stretch to parent (parent already enforces A4 ratio). */
  fill?: boolean;
}> = ({ children, footer, pageNumber, pageCount, fill = false }) => (
  <div
    className={`
      relative overflow-hidden bg-white text-gray-800
      shadow-[0_10px_40px_rgba(15,23,42,0.08)]
      ring-1 ring-black/5
      ${fill ? 'h-full w-full' : 'aspect-[210/297] w-full max-w-[210mm] shrink-0'}
    `}
  >
    <div className="absolute inset-0 flex flex-col px-[12mm] py-[12mm] text-[10pt] leading-snug">
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
      {footer ? <div className="mt-auto shrink-0 pt-6">{footer}</div> : null}
      {pageCount > 1 ? (
        <div
          className={`shrink-0 pt-3 text-right text-[8pt] tabular-nums text-gray-400${
            footer ? '' : ' mt-auto'
          }`}
        >
          Page {pageNumber}/{pageCount}
        </div>
      ) : null}
    </div>
  </div>
);

const InvoicePreview: React.FC<{ model: InvoicePreviewModel; sticky?: boolean }> = ({
  model,
  sticky = false,
}) => {
  const L = model.labels;
  const pages = useMemo(
    () => paginateLineItems(model.lineItems, estimateFooterUnits(model)),
    [model]
  );
  const pageCount = pages.length;
  const multiPage = pageCount > 1;
  const documentTitle = model.invoiceName?.trim() || model.invoiceNumber.trim() || '—';
  const showVat = model.lineItems.some(
    (line) => line.vatRate !== null && line.vatRate !== undefined
  );

  const renderPageBody = (page: PreviewPage) => (
    <>
      {page.showDocumentHeader ? (
        <>
          <div>
            <div className="text-[22pt] font-bold leading-none tracking-tight text-gray-950">
              {documentTitle}
            </div>
            {model.invoiceName?.trim() && model.invoiceNumber.trim() ? (
              <div className="mt-2 text-[10pt] text-gray-500">
                {model.invoiceNumber.trim()}
              </div>
            ) : null}
          </div>

          <div className="mt-7 grid grid-cols-2 gap-6">
            <PartyBlock party={model.seller} labels={L} />
            <PartyBlock party={model.buyer} labels={L} />
          </div>

          <div className="mt-6 border-y border-gray-100 py-3">
            <div className="text-[8pt] font-semibold uppercase tracking-wide text-gray-400">
              {L.issuedAt}
            </div>
            <div className="mt-0.5 text-gray-900">{model.issuedAtLabel || '—'}</div>
          </div>
        </>
      ) : (
        <div className="mb-4 border-b border-gray-100 pb-3">
          <div className="truncate text-[11pt] font-semibold text-gray-950">
            {documentTitle}
          </div>
          {model.invoiceNumber.trim() ? (
            <div className="mt-0.5 text-[9pt] text-gray-500">
              {model.invoiceNumber.trim()}
            </div>
          ) : null}
        </div>
      )}

      {page.items.length > 0 ? (
        <div className={page.showDocumentHeader ? 'mt-5' : 'mt-1'}>
          <LineItemsTable items={page.items} labels={L} showVat={showVat} />
        </div>
      ) : null}
    </>
  );

  const pageFooter = (page: PreviewPage) =>
    page.showFooter ? <InvoiceFooter model={model} /> : null;

  const anchorRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [fixedBox, setFixedBox] = useState<{ left: number; width: number } | null>(null);
  const [pageSize, setPageSize] = useState<{ w: number; h: number } | null>(null);
  // top-4 + create bar + constant gap under preview
  const panelHeight = 'calc(100dvh - 5.75rem)';

  useLayoutEffect(() => {
    if (!sticky) {
      setFixedBox(null);
      return;
    }
    const anchor = anchorRef.current;
    if (!anchor) return;

    const update = () => {
      if (!window.matchMedia('(min-width: 1280px)').matches) {
        setFixedBox(null);
        return;
      }
      const rect = anchor.getBoundingClientRect();
      setFixedBox({ left: rect.left, width: rect.width });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(anchor);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [sticky]);

  useLayoutEffect(() => {
    if (!sticky || !fixedBox) {
      setPageSize(null);
      return;
    }
    const body = bodyRef.current;
    if (!body) return;

    const update = () => {
      const style = getComputedStyle(body);
      const padX =
        (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const padY =
        (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      const availW = body.clientWidth - padX;
      const availH = body.clientHeight - padY;
      if (availW < 80) return;

      const ratio = 210 / 297;

      if (multiPage) {
        // Full A4 sheets stacked — width-fit, never compress height (scroll instead).
        const w = availW;
        setPageSize({ w, h: w / ratio });
        return;
      }

      if (availH < 80) return;
      let h = availH;
      let w = h * ratio;
      if (w > availW) {
        w = availW;
        h = w / ratio;
      }
      setPageSize({ w, h });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(body);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [sticky, multiPage, fixedBox, pages]);

  const panel = (
    <div
      className={`overflow-hidden rounded-2xl bg-gray-100 ring-1 ring-inset ring-gray-200/80${
        sticky ? ' flex h-full flex-col' : ''
      }`}
    >
      <div className="flex shrink-0 items-center justify-between gap-2 px-3 pt-3 sm:px-4 sm:pt-4">
        <h4 className="text-base font-semibold text-gray-900">{L.preview}</h4>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-md bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 ring-1 ring-inset ring-gray-200/80">
            A4{multiPage ? ` · ${pageCount}` : ''}
          </span>
        </div>
      </div>

      <div
        ref={bodyRef}
        className={`mt-3 flex flex-col items-center gap-4 px-3 pb-3 sm:px-4 sm:pb-4${
          sticky
            ? multiPage
              ? ' min-h-0 flex-1 overflow-y-auto'
              : ' min-h-0 flex-1 overflow-hidden'
            : ''
        }`}
      >
        {sticky && pageSize ? (
          pages.map((page, pageIndex) => (
            <div
              key={`page-${pageIndex}`}
              className="shrink-0"
              style={{ width: pageSize.w, height: pageSize.h }}
            >
              <A4Page
                pageNumber={pageIndex + 1}
                pageCount={pageCount}
                footer={pageFooter(page)}
                fill
              >
                {renderPageBody(page)}
              </A4Page>
            </div>
          ))
        ) : (
          pages.map((page, pageIndex) => (
            <A4Page
              key={`page-${pageIndex}`}
              pageNumber={pageIndex + 1}
              pageCount={pageCount}
              footer={pageFooter(page)}
            >
              {renderPageBody(page)}
            </A4Page>
          ))
        )}
      </div>
    </div>
  );

  if (!sticky) {
    return <div className="min-w-0">{panel}</div>;
  }

  return (
    <>
      {/* Reserves the grid column; panel is fixed so it never unsticks at page end */}
      <div ref={anchorRef} className="hidden min-w-0 xl:block" aria-hidden />
      {fixedBox ? (
        <div
          className="pointer-events-auto fixed z-10"
          style={{
            top: '1rem',
            left: fixedBox.left,
            width: fixedBox.width,
            height: panelHeight,
          }}
        >
          {panel}
        </div>
      ) : (
        <div className="min-w-0">{panel}</div>
      )}
    </>
  );
};

export default InvoicePreview;
